/** Request a tenant close, a code release or a reopen (tenant retirement CONTRACT §8.3, §14 B7): a
 *  reason and the tenant's code typed again (exactly, case-sensitive — the backend checks it too).
 *  Nothing changes on the tenant yet: the request waits for an approval (by someone else when
 *  two-person approval is on). A refusal is put in words in the dialog; one about a field marks
 *  that field.
 *
 *  A release first shows its gate: the waiting period after the close (`release_min_closed_days`,
 *  `earliest_release_at`) and whatever else blocks it now (`release_blockers`, the offboarding
 *  items linking to the Offboarding tab). While the waiting period runs, a superuser (`is_superuser`
 *  of the signed-in user) may override it with a reason, recorded on the request; anyone else sees
 *  the date only. The backend re-checks all of it. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  LIFECYCLE_REASON_MAX,
  lifecycleApi,
  lifecycleError,
  lifecycleErrorWords,
  type TenantLifecycleRequestKind,
  type TenantLifecycleState,
} from '@/api/tenantLifecycle';
import { useAuth } from '@/auth/AuthProvider';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { distinctBlockers, LifecycleBlockerList } from './LifecycleBlockerList';
import { useLifecycleRefresh } from './useLifecycleRefresh';

/** The kinds this dialog asks for. */
export type RequestableKind = TenantLifecycleRequestKind;

/** Reason + the code typed again. Shared by the request and the approve dialogs. */
export function confirmSchema(code: string) {
  return z.object({
    reason: z
      .string()
      .trim()
      .min(1, 'Say why — it is kept with the request and in the audit record.')
      .max(LIFECYCLE_REASON_MAX, `At most ${LIFECYCLE_REASON_MAX} characters.`),
    confirm_code: z.string().refine((v) => v === code, {
      message: `Type ${code} exactly (case-sensitive).`,
    }),
  });
}
export type ConfirmValues = z.infer<ReturnType<typeof confirmSchema>>;

/** The request form: the confirmation, plus a release's override of the waiting period. */
function requestSchema(code: string) {
  return confirmSchema(code)
    .extend({
      override: z.boolean(),
      override_reason: z
        .string()
        .max(LIFECYCLE_REASON_MAX, `At most ${LIFECYCLE_REASON_MAX} characters.`),
    })
    .superRefine((v, ctx) => {
      if (v.override && !v.override_reason.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['override_reason'],
          message: 'Say why the waiting period is overridden — it is recorded and audited.',
        });
      }
    });
}
type RequestValues = z.infer<ReturnType<typeof requestSchema>>;

const EMPTY: RequestValues = { reason: '', confirm_code: '', override: false, override_reason: '' };

const TITLE: Record<RequestableKind, string> = {
  close: 'Request to close the tenant',
  release: 'Request to release the code',
  reopen: 'Request to reopen the tenant',
};

const SUBMIT: Record<RequestableKind, string> = {
  close: 'Request close',
  release: 'Request release',
  reopen: 'Request reopen',
};

const REQUESTED: Record<RequestableKind, string> = {
  close: 'Close',
  release: 'Code release',
  reopen: 'Reopen',
};

/** What an approval of `kind` will do to this tenant. */
function whatItDoes(kind: RequestableKind, state: TenantLifecycleState): string {
  switch (kind) {
    case 'close':
      return 'Once approved, the tenant stops everywhere at once: its users are signed out and refused, its API keys and MCP access stop working, webhooks are ignored, sends and tasks are dropped, and its sandbox is suspended. Its data stays, and it can be reopened.';
    case 'release':
      return `Once approved, a job gives up the code ${state.code}: the tenant takes a placeholder code, its imported ids are re-keyed, what is stored under the code is purged and its API keys are revoked — its sandbox's too. Another tenant may then take ${state.code}. The tenant stays closed, and can get its code back only while no other tenant holds it.`;
    case 'reopen':
      return state.lifecycle === 'released'
        ? `Once approved, a job gives the tenant its code ${state.released_code ?? ''} back and reopens it: its users sign in again, webhooks and sends work again, and its sandbox resumes. API keys revoked by the release are not restored — they are issued again by hand, like the e-mail domain, Airbnb, Twilio numbers and Firebase testers.`
        : 'Once approved, the tenant is active again: its users sign in again, API keys, webhooks and sends work again, and its sandbox resumes. Some set-up is redone by hand afterwards — the e-mail domain, Airbnb, Twilio numbers, Firebase testers.';
  }
}

interface Props {
  state: TenantLifecycleState;
  /** The kind to ask for; null keeps the dialog closed. */
  kind: RequestableKind | null;
  onClose: () => void;
}

export function RequestLifecycleDialog({ state, kind, onClose }: Props) {
  const snackbar = useSnackbar();
  const refresh = useLifecycleRefresh();
  const { user } = useAuth();
  const topId = state.tour_operator_id;
  const code = state.code;
  const schema = useMemo(() => requestSchema(code), [code]);
  const { control, register, handleSubmit, reset, setError, watch, formState } = useForm<RequestValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  // A release's gate, as the state last read it (the backend judges it again).
  const releasing = kind === 'release';
  const blockers = releasing ? distinctBlockers(state.release_blockers) : [];
  const tooEarly = blockers.some((b) => b.code === 'release_too_early');
  const otherBlockers = blockers.filter((b) => b.code !== 'release_too_early');
  const superuser = user?.is_superuser === true;
  const canOverride = tooEarly && superuser;
  const override = watch('override') && canOverride;
  const gateClosed = releasing && (otherBlockers.length > 0 || (tooEarly && !override));

  const mut = useMutation({
    mutationFn: (v: RequestValues & { kind: RequestableKind }) =>
      lifecycleApi.request(topId, {
        kind: v.kind,
        reason: v.reason.trim(),
        confirm_code: v.confirm_code,
        ...(v.kind === 'release' && v.override ? { override_reason: v.override_reason.trim() } : {}),
      }),
    onSuccess: (_r, v) => {
      snackbar.show(`${REQUESTED[v.kind]} requested — nothing changes until it is approved.`, 'success');
      refresh();
      onClose();
    },
    onError: (e) => {
      const code = lifecycleError(e)?.code;
      if (code === 'confirm_code_mismatch') setError('confirm_code', { message: lifecycleErrorWords(e) });
      if (code === 'reason_required') setError('reason', { message: lifecycleErrorWords(e) });
      // Another request opened, or the tenant or its gate changed meanwhile: show what is true now.
      if (
        code === 'lifecycle_request_open' ||
        code === 'not_active' ||
        code === 'not_closed' ||
        code === 'lifecycle_in_progress' ||
        code === 'release_too_early' ||
        code === 'offboarding_incomplete' ||
        code === 'work_running' ||
        code === 'code_taken'
      )
        refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (!kind) return;
    reset(EMPTY);
    resetMutation();
  }, [kind, reset, resetMutation]);

  const errorCode = lifecycleError(mut.error)?.code;
  const showError =
    mut.error != null && errorCode !== 'confirm_code_mismatch' && errorCode !== 'reason_required';
  const submit = handleSubmit((v) =>
    kind && mut.mutate({ ...v, override: kind === 'release' && canOverride && v.override, kind }),
  );
  const earliest = state.earliest_release_at;

  return (
    <AppDialog
      open={kind != null}
      onClose={onClose}
      title={kind ? TITLE[kind] : ''}
      disableBackdropClose={mut.isPending || formState.isDirty}
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={kind === 'reopen' ? 'primary' : 'error'}
            disabled={mut.isPending || gateClosed}
            onClick={() => void submit()}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {kind ? SUBMIT[kind] : 'Request'}
          </Button>
        </>
      }
    >
      {kind && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Typography variant="body2">{whatItDoes(kind, state)}</Typography>
          {releasing && (
            <Box role="group" aria-label="Release gate" sx={{ display: 'grid', gap: 1 }}>
              <Typography variant="body2">
                A code release needs the tenant closed for {state.release_min_closed_days} days
                {earliest
                  ? ` — ${tooEarly ? 'not before' : 'possible since'} ${formatDate(earliest)} (${formatRelativeTime(earliest)})`
                  : ''}
                .
              </Typography>
              {tooEarly && !superuser && (
                <Alert severity="info">
                  The waiting period is not over: the code can be released from{' '}
                  {earliest ? formatDate(earliest) : 'the end of the waiting period'}. Only a superuser
                  can override it.
                </Alert>
              )}
              {canOverride && (
                <>
                  <Controller
                    control={control}
                    name="override"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            disabled={mut.isPending}
                          />
                        }
                        label={`Override the ${state.release_min_closed_days}-day period`}
                      />
                    )}
                  />
                  {override && (
                    <TextField
                      {...register('override_reason')}
                      label="Override reason"
                      required
                      multiline
                      minRows={2}
                      size="small"
                      disabled={mut.isPending}
                      inputProps={{ maxLength: LIFECYCLE_REASON_MAX }}
                      error={Boolean(formState.errors.override_reason)}
                      helperText={
                        formState.errors.override_reason?.message ??
                        'Why the code cannot wait — recorded on the request and audited.'
                      }
                    />
                  )}
                </>
              )}
              {otherBlockers.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>What blocks a release now</AlertTitle>
                  <LifecycleBlockerList
                    topId={topId}
                    blockers={otherBlockers}
                    label="What blocks a release now"
                  />
                </Alert>
              )}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            {state.two_person_required
              ? 'Two-person approval is on: another ops user approves the request.'
              : 'You may approve the request yourself, as a second step.'}
          </Typography>
          <TextField
            {...register('reason')}
            label="Reason"
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
            disabled={mut.isPending}
            inputProps={{ maxLength: LIFECYCLE_REASON_MAX }}
            error={Boolean(formState.errors.reason)}
            helperText={formState.errors.reason?.message ?? 'Kept with the request and in the audit record.'}
          />
          <TextField
            {...register('confirm_code')}
            label="Tenant code"
            required
            size="small"
            disabled={mut.isPending}
            autoComplete="off"
            inputProps={{ spellCheck: false, maxLength: 64 }}
            error={Boolean(formState.errors.confirm_code)}
            helperText={formState.errors.confirm_code?.message ?? `Type ${code} to confirm.`}
          />
          {showError && (
            <Alert severity="error">
              {lifecycleErrorWords(mut.error)}
            </Alert>
          )}
        </Box>
      )}
    </AppDialog>
  );
}
